import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Alert, Box, Button, Paper, Stack, TextField, Typography } from "@mui/material";

import CustomSnackBar from "../../Components/CustomSnackBar";
import MainText from "../../Components/MainText";
import { hasPermission } from "../../config/permissionUtils";
import { useAuth } from "../../context/AuthContext";
import { groomingService } from "../../services";

const buildInitialForm = () => ({
  business_start: "08:00",
  business_end: "18:00",
  slot_minutes: "30",
  max_pets_per_slot: "1",
});

const buildSlotPreview = (startValue, endValue, slotMinutesValue) => {
  const [startHour, startMinute] = String(startValue || "").split(":").map((row) => Number(row || 0));
  const [endHour, endMinute] = String(endValue || "").split(":").map((row) => Number(row || 0));
  const slotMinutes = Math.max(1, Number(slotMinutesValue || 0));
  if (!Number.isInteger(startHour) || !Number.isInteger(startMinute)) return [];
  if (!Number.isInteger(endHour) || !Number.isInteger(endMinute)) return [];

  const start = (startHour * 60) + startMinute;
  const end = (endHour * 60) + endMinute;
  if (start >= end) return [];

  const values = [];
  for (let minuteMark = start; minuteMark < end; minuteMark += slotMinutes) {
    const hh = Math.floor(minuteMark / 60);
    const mm = minuteMark % 60;
    values.push(`${String(hh).padStart(2, "0")}:${String(mm).padStart(2, "0")}`);
  }
  return values;
};

const extractErrorMessage = (error, fallback = "No se pudo completar la operacion.") => {
  const data = error?.response?.data;
  if (!data || typeof data !== "object") return fallback;
  if (typeof data.detail === "string" && data.detail.trim()) return data.detail;
  const firstKey = Object.keys(data)[0];
  const firstValue = data[firstKey];
  if (Array.isArray(firstValue) && firstValue[0]) return String(firstValue[0]);
  if (typeof firstValue === "string" && firstValue.trim()) return firstValue;
  return fallback;
};

function GroomingConfigPage() {
  const { currentBranch, effectivePerms = [], isSuperuser } = useAuth();
  const branchId = currentBranch?.id || null;
  const canManage = hasPermission(effectivePerms, "grooming_settings_manage", isSuperuser);
  const canViewPage = canManage;

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(buildInitialForm());
  const [snackOpen, setSnackOpen] = useState(false);
  const [snackMsg, setSnackMsg] = useState("");
  const [snackSev, setSnackSev] = useState("info");

  const showSnack = useCallback((message, severity = "info") => {
    setSnackMsg(message);
    setSnackSev(severity);
    setSnackOpen(true);
  }, []);

  const loadConfig = useCallback(async () => {
    if (!branchId || !canViewPage) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const data = await groomingService.getBusinessConfig({ branch_id: branchId });
      setForm({
        business_start: String(data?.business_start || "08:00"),
        business_end: String(data?.business_end || "18:00"),
        slot_minutes: String(data?.slot_minutes || "30"),
        max_pets_per_slot: String(data?.max_pets_per_slot || "1"),
      });
    } catch (error) {
      showSnack(extractErrorMessage(error, "No se pudo cargar la configuracion grooming."), "error");
    } finally {
      setLoading(false);
    }
  }, [branchId, canViewPage, showSnack]);

  useEffect(() => {
    loadConfig();
  }, [loadConfig]);

  const slotPreview = useMemo(
    () => buildSlotPreview(form.business_start, form.business_end, form.slot_minutes),
    [form.business_end, form.business_start, form.slot_minutes]
  );

  const handleSave = async () => {
    if (!branchId || !canManage) return;
    const slotMinutes = Number(form.slot_minutes);
    const maxPets = Number(form.max_pets_per_slot);
    if (!Number.isInteger(slotMinutes) || slotMinutes <= 0) {
      showSnack("slot_minutes debe ser mayor que 0.", "warning");
      return;
    }
    if (!Number.isInteger(maxPets) || maxPets <= 0) {
      showSnack("max_pets_per_slot debe ser mayor que 0.", "warning");
      return;
    }

    setSaving(true);
    try {
      await groomingService.updateBusinessConfig({
        branch_id: branchId,
        business_start: form.business_start,
        business_end: form.business_end,
        slot_minutes: slotMinutes,
        max_pets_per_slot: maxPets,
      });
      showSnack("Configuracion grooming guardada.", "success");
      await loadConfig();
    } catch (error) {
      showSnack(extractErrorMessage(error, "No se pudo guardar la configuracion."), "error");
    } finally {
      setSaving(false);
    }
  };

  if (!canViewPage) {
    return <Alert severity="warning">No tienes permisos para ver grooming.</Alert>;
  }

  if (!branchId) {
    return <Alert severity="info">Selecciona una sucursal para operar grooming.</Alert>;
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
      <MainText title="Configuracion grooming" subtitle="Horario, slots y capacidad por sucursal" />

      <Paper variant="outlined" sx={{ p: 2 }}>
        <Stack spacing={1.25}>
          <Stack direction={{ xs: "column", md: "row" }} spacing={1.25}>
            <TextField
              label="Inicio"
              type="time"
              value={form.business_start}
              onChange={(event) => setForm((prev) => ({ ...prev, business_start: event.target.value }))}
              InputLabelProps={{ shrink: true }}
              fullWidth
              disabled={!canManage || loading || saving}
            />
            <TextField
              label="Fin"
              type="time"
              value={form.business_end}
              onChange={(event) => setForm((prev) => ({ ...prev, business_end: event.target.value }))}
              InputLabelProps={{ shrink: true }}
              fullWidth
              disabled={!canManage || loading || saving}
            />
          </Stack>

          <Stack direction={{ xs: "column", md: "row" }} spacing={1.25}>
            <TextField
              label="Minutos por slot"
              value={form.slot_minutes}
              onChange={(event) => setForm((prev) => ({ ...prev, slot_minutes: event.target.value }))}
              type="number"
              inputProps={{ min: 1, step: 1 }}
              fullWidth
              disabled={!canManage || loading || saving}
            />
            <TextField
              label="Mascotas por slot"
              value={form.max_pets_per_slot}
              onChange={(event) => setForm((prev) => ({ ...prev, max_pets_per_slot: event.target.value }))}
              type="number"
              inputProps={{ min: 1, step: 1 }}
              fullWidth
              disabled={!canManage || loading || saving}
            />
          </Stack>

          <Typography variant="body2" color="text.secondary">
            Slots calculados: {slotPreview.length} {slotPreview.length === 1 ? "slot" : "slots"}.
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {slotPreview.slice(0, 10).join(" · ")}
            {slotPreview.length > 10 ? " ..." : ""}
          </Typography>

          <Stack direction="row" justifyContent="flex-end">
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={!canManage || loading || saving}
            >
              {saving ? "Guardando..." : "Guardar"}
            </Button>
          </Stack>
        </Stack>
      </Paper>

      <CustomSnackBar open={snackOpen} setOpen={setSnackOpen} message={snackMsg} severity={snackSev} />
    </Box>
  );
}

export default GroomingConfigPage;
